import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import { CSSObject, styled, Theme, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import { useState } from 'react';
import { Outlet } from 'react-router';

import { api } from '../../../api';
import { AppName } from '../../../constants';
import { GroupedNavItems } from '../../../constants/NavItems';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxStoreHooks';
import { logOutUser } from '../../../store/authSlice';
import Logo from '../../Logo';
import NavItemButton from '../NavItemButton';

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  has_root_company?: number;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, has_root_company }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: has_root_company ? drawerWidth : 0,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));
export default function DesktopNav() {
  const theme = useTheme();
  const userId = useAppSelector((state) => state.auth.userId);

  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: `100%`,
      }}
    >
      <AppBar position='fixed' open={open} color='inherit'>
        <Toolbar>
          <IconButton
            color='inherit'
            aria-label='open drawer'
            onClick={handleDrawerOpen}
            edge='start'
            sx={[
              {
                marginRight: 4,
              },
              open && { display: 'none' },
            ]}
          >
            <MenuIcon />
          </IconButton>

          <Stack
            sx={{ width: '100%' }}
            alignItems='center'
            justifyContent='space-between'
            direction='row'
          >
            <Stack direction='row' alignItems='center' gap={1}>
              <Logo />
              <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                {AppName}
              </Typography>
            </Stack>

            {/* <Stack direction='row' gap={1} alignItems='center'>
              Account popover
            </Stack> */}
            <Stack gap={2} direction='row' alignItems='center'>
              <Typography sx={{ fontWeight: 'bold' }}>
                {userId === 1 ? 'Testni korisnik' : 'Dušan Pantelić'}
              </Typography>
              <Button
                variant='outlined'
                onClick={() => {
                  dispatch(logOutUser());
                  dispatch(api.util.resetApiState());
                }}
              >
                Izloguj se
              </Button>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer variant='permanent' open={open}>
        <DrawerHeader sx={{ height: 100 }}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>

        <Divider />
        <List sx={{ px: 1, mt: 1 }}>
          {Object.entries(GroupedNavItems).map(([groupTitle, items]) => (
            <Stack key={items[0].id}>
              {groupTitle !== '__ungrouped' && open && (
                <Typography
                  variant='subtitle2'
                  sx={{
                    px: 2,
                    my: 1,
                    fontWeight: 'bold',
                  }}
                >
                  {groupTitle}
                </Typography>
              )}
              {groupTitle !== '__ungrouped' && !open && (
                <Typography
                  variant='subtitle2'
                  sx={{
                    px: 2,
                    my: 1,
                    fontWeight: 'bold',
                  }}
                >
                  ---
                </Typography>
              )}
              {items.map((navItem) => (
                <Stack key={navItem.id}>
                  <Tooltip
                    arrow
                    title={navItem.label}
                    placement='right'
                    disableHoverListener={open}
                  >
                    <Box>
                      <NavItemButton
                        navItem={navItem}
                        open={open}
                        setClose={() => setOpen(false)}
                      />
                      {navItem.addDividerAfter && <Divider sx={{ my: 1 }} />}
                    </Box>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          ))}
        </List>
      </Drawer>

      <Box
        component='main'
        data-testid='DesktopNav'
        sx={{
          mt: 16,
          mb: 10,
          width: '100%',
          overflow: 'auto',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
